import React from 'react'
import { useIntl } from 'gatsby-plugin-intl'

import Layout from 'components/layout'
import MetaTags from 'components/MetaTags'
import Header from 'components/Header'

import styled from 'styled-components'

import { media } from 'styles/media'
import { greyLight, greyDark } from 'styles/colors'
import * as spacing from 'styles/spacing'

import Heading from 'components/Heading'
import List from 'components/List'
import PhotoGallery from 'components/PhotoGallery'
import Section, { SectionBlock } from 'components/Section'
import Wrapper from 'components/Wrapper'
import GridRow from 'components/grid/GridRow'
import GridItem from 'components/grid/GridItem'
import BookButton from 'components/button/BookButton'
import Button from 'components/button/Button'
import SmallCaps from 'components/SmallCaps'
import Icon from 'components/Icon'

import apartmentsContent from 'content/apartmentsContent.json'
import { apartmentAltPath } from 'content/docAlternatePaths'

function findApartment(locale, slug) {
  for (const cat of apartmentsContent[locale] || []) {
    const a = (cat.apartments || []).find((x) => x.slug === slug)
    if (a) return a
  }
  return null
}

const Container = styled.div`
  position: static;
`

const Main = styled.main`
  position: static;
  max-width: 700px;

  small {
    display: block;
    color: ${greyLight};
  }

  ${media.medium`
    padding-right: ${spacing.xLarge};
    max-width: none;
  `};

  ${media.large`
    padding-right: ${spacing.xxxLarge};
  `};

  ${media.xLarge`
    padding-right: 88px;
  `};
`

const Meta = styled.div`
  margin-bottom: ${spacing.large};

  ${media.xLarge`
    margin-bottom: ${spacing.xLarge};
  `};
`

const MetaItemLabel = styled(SmallCaps)`
  color: ${greyDark};
`

const MetaItemIconWrap = styled.div`
  display: flex;
  margin-right: ${spacing.small};
  color: ${greyLight};
`

const MetaItemContainer = styled.div`
  display: inline-flex;
  align-items: center;

  margin-right: ${spacing.medium};

  ${media.medium`
      margin-right: ${spacing.large};
    `};

  ${media.xLarge`
      margin-right: ${spacing.xLarge};
    `};
`

const MetaItem = ({ icon, label }) => (
  <MetaItemContainer>
    <MetaItemIconWrap>
      <Icon icon={icon} size='small' />
    </MetaItemIconWrap>
    <MetaItemLabel>{label}</MetaItemLabel>
  </MetaItemContainer>
)

const ApartmentTemplate = ({ pageContext }) => {
  const intl = useIntl()
  const slug = pageContext.slug
  const apartment = findApartment(intl.locale, slug)

  const pageAlternativeLangPath = apartmentAltPath(intl.locale, slug)

  if (!apartment) {
    return (
      <Layout>
        <MetaTags title='Hotel Griffintown' />
        <Wrapper>
          <p>Not found.</p>
        </Wrapper>
      </Layout>
    )
  }

  const occupancyString = (apartment.occupancy || [])
    .map(({ adult, child }) =>
      intl.formatMessage(
        { id: 'occupancyCount' },
        {
          adultCount: Number(adult),
          childCount: Number(child),
        }
      )
    )
    .join(` ${intl.formatMessage({ id: 'or' })} `)

  const gallery = apartment.galleryUrls || []

  return (
    <Layout>
      <MetaTags
        title={`${apartment.seoTitle} | ${intl.formatMessage({
          id: 'apartment',
        })} | Hotel Griffintown`}
        description={apartment.seoDescription}
      />

      <Header
        eyebrow={intl.formatMessage({ id: 'apartment' })}
        heading={apartment.title}
        heroImageUrl={apartment.heroImageUrl}
        pageAlternativeLangPath={pageAlternativeLangPath}
      />

      <Container>
        <Section>
          <Wrapper size='narrow'>
            <GridRow>
              <GridItem xs='1-1' md='2-3'>
                <Main>
                  <Heading size={4} variant='sans'>
                    {intl.formatMessage({ id: 'suites.overview' })}
                  </Heading>
                  <Meta>
                    {(apartment.bedTypes || []).map((bed, idx) => (
                      <MetaItem
                        key={idx}
                        icon='bed'
                        label={`${bed.quantity} ${intl.formatMessage({
                          id: bed.typeKey,
                        })}`}
                      />
                    ))}

                    {apartment.size ? (
                      <MetaItem
                        icon='size'
                        label={`${apartment.size} ${intl.formatMessage({
                          id: 'sqft',
                        })}`}
                      />
                    ) : null}

                    {occupancyString ? (
                      <MetaItem icon='occupants' label={occupancyString} />
                    ) : null}
                  </Meta>

                  {apartment.overviewHtml ? <p>{apartment.overviewHtml}</p> : null}

                  <BookButton
                    url={apartment.bookingUrl}
                    target={apartment.bookingTarget}
                  />
                </Main>
              </GridItem>
              <GridItem xs='1-1' md='1-3'>
                <List
                  items={(apartment.amenityIds || []).map((id) =>
                    intl.formatMessage({ id })
                  )}
                />
              </GridItem>
            </GridRow>
          </Wrapper>
        </Section>
        <Section flushTop>
          <Wrapper>
            {gallery.length > 0 ? (
              <SectionBlock>
                <PhotoGallery photos={gallery} />
              </SectionBlock>
            ) : null}
            <SectionBlock>
              <div
                css={`
                  text-align: center;
                `}
              >
                <Button to='/apartments' theme='secondary'>
                  {intl.formatMessage({ id: 'home.suites.explore' })}
                </Button>
              </div>
            </SectionBlock>
          </Wrapper>
        </Section>
      </Container>
    </Layout>
  )
}

export default ApartmentTemplate
